import { Store } from '@ngrx/store';
import { ContentPageId } from '@shared/models';
import { LogError } from '@store/actions/logger/logger.actions';
import { State } from '@store/index';

import { EnvName } from '../environments/environment.interface';
import { config, MigratedPages, migratedPages } from './config';

function isMigratedPage(page: ContentPageId): page is MigratedPages {
  return page in migratedPages;
}

export const getAEMPageUrl = (page: ContentPageId, env: EnvName): string | undefined => {
  if (!isMigratedPage(page)) {
    return undefined;
  }
  switch (env) {
    case 'local.external':
    case 'local.internal':
    case 'local.test.external':
    case 'local.test-2.external':
    case 'local.test-3.external':
    case 'local.test-4.external':
    case 'eng.internal':
    case 'eng.external':
    case 'test.internal':
    case 'test.external':
    case 'test-2.external':
    case 'test-3.external':
    case 'test-4.external':
    case 'e2e':
    case 'e2e-templates':
      return config[page].urls.test;
    case 'prod.internal':
    case 'prod.external':
      return config[page].urls.prod;
    default:
      // Exhaustive check
      const n: never = env;
      throw new Error('INVALID_AEM_ENVIRONMENT');
  }
};

function safeGet(
  getter: () => string | undefined | null,
  pageName: string,
  flowType: string,
  label?: string,
  store?: Store<State>,
): string {
  try {
    const value = getter();
    if (value == null) {
      // covers both null and undefined
      throw new Error(
        `Content value is null or undefined for ${label ?? 'unknown label'} on ${pageName}`,
      );
    }
    return value;
  } catch (err) {
    if (store) {
      store.dispatch(
        LogError({
          payload: {
            eventId: 'aem-content-not-found',
            pageName: pageName,
            message: `Failed to get content on ${pageName} page for label ${label ?? 'unknown label'}`,
            flowType: flowType,
            data: err,
          },
        }),
      );
    }
    // Exception handled: logging to Splunk and returning fallback for UI
    return '';
  }
}

export function createSafeGetFactory(pageName: string, flowType: string, store?: Store<State>) {
  return function (getter: () => string | undefined | null, label?: string): string {
    return safeGet(getter, pageName, flowType, label, store);
  };
}
